import type {AddSubtitleData, AddWordData} from '@Libraries/add-media-library';
import {ElementDataType} from '@Libraries/add-media-library';
import type {GeneratedFromType, TranscriptGeneratedFrom, TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {MIN_TIME_GAP_FOR_TRANSCRIPT} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {AiTranscriptSupportedLanguage} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {AiTranscriptForItemWithParams} from '@Hooks/poster-editor/useAiTranscriptGeneration';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {WordObject} from '@PosterWhiteboard/items/transcript-item/subtitle/word/word.types';
import {getUniqueString, removePunctuationFromWord} from '@Utils/string.util';
import type {SelectableItemWithAudio} from '@Panels/ai-subtitle-panel/ai-subtitle-panel.types';
import {repoVideoThumbImageURL} from '@Libraries/user-video-library';
import {getRandomThumbnailImageForAudioItem} from '@Libraries/poster-audio-item.library';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import styles from '@Components/add-ai-item-button/add-ai-item-button.module.scss';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';

export const NO_SPEECH_DETECTED_ERROR_CODE = 'NO_SPEECH_DETECTED';

export enum AiSubtitleMenuOpenedFrom {
  ADD_AI_OPTION = 'add_ai_option',
  ADD_TEXT_OPTION = 'add_text_option',
}

export interface GeneratedTranscriptWordsData {
  id: string;
  start: number;
  end: number;
  text: string;
}

export interface GeneratedTranscriptSubtitlesData {
  id: string;
  start: number;
  end: number;
  text: string;
  words: GeneratedTranscriptWordsData[];
}

export interface GeneratedAiTranscriptData {
  text: string;
  subtitles: GeneratedTranscriptSubtitlesData[];
}

export interface AiTranscriptGenerationResponse {
  transcript?: GeneratedAiTranscriptData;
  error?: string;
}

interface OrderedPage {
  id: string;
  start: number;
}

export const doesTranscriptForItemNeedRegeneration = (params: AiTranscriptForItemWithParams, existingTranscriptItems: TranscriptItemObject[]): boolean => {
  return (
    !doTrimTimesMatchGeneratedTranscriptForItems(params.startTime, params.endTime, params.onPosterStartTime, existingTranscriptItems) ||
    doesAnyTranscriptHaveUserEditedSubtitles(existingTranscriptItems) ||
    hasSpeedBeenChangedAfterAiTranscriptGenerationForItems(params.speed, existingTranscriptItems) ||
    hasLoopCountChangedAfterAiTranscriptGenerationForVideoItems(params.loopCycles, existingTranscriptItems)
  );
};

export const doTrimTimesMatchGeneratedTranscriptForItems = (
  trimStartTime: number,
  trimEndTime: number,
  onPosterStartTime: number,
  generatedTranscripts: TranscriptItemObject[]
): boolean => {
  if (!generatedTranscripts.length) {
    return false;
  }

  return generatedTranscripts.every((transcript) => doTrimTimesMatchGeneratedTranscript(trimStartTime, trimEndTime, onPosterStartTime, transcript));
};

export const doTrimTimesMatchGeneratedTranscript = (trimStartTime: number, trimEndTime: number, onPosterStartTime: number, generatedTranscript: TranscriptItemObject): boolean => {
  if (!generatedTranscript.generatedFrom) {
    return false;
  }
  const generatedFromStartTime = generatedTranscript.generatedFrom.startTime;
  const generatedFromEndTime = generatedTranscript.generatedFrom.endTime;
  const generatedFromOnPosterStartTime = generatedTranscript.generatedFrom.onPosterStartTime;

  return trimStartTime === generatedFromStartTime && trimEndTime === generatedFromEndTime && onPosterStartTime === generatedFromOnPosterStartTime;
};

export const doesAnyTranscriptHaveUserEditedSubtitles = (transcripts: TranscriptItemObject[]): boolean => {
  return transcripts.some(doesTranscriptHaveUserEditedSubtitles);
};

export const generateAiTranscriptAndGetSubtitles = async (
  audioHashedFilename: string,
  trimStartTime: number,
  trimEndTime: number,
  mediaType: GeneratedFromType,
  loopCycles = 1,
  speed = 1,
  language = AiTranscriptSupportedLanguage.ENGLISH
): Promise<GeneratedAiTranscriptData> => {
  const res = (await window.PMW.pollingAjaxCallAsync(
    generateAiTranscript.bind(null, audioHashedFilename, trimStartTime, trimEndTime, mediaType, loopCycles, speed, language),
    checkAiTranscriptGenerationStatus.bind(null)
  )) as AiTranscriptGenerationResponse;

  if (res.error) {
    throw new Error(res.error);
  }

  if (res.transcript) {
    return res.transcript;
  }

  throw new Error('Neither error, nor transcript received in response!');
};

const checkAiTranscriptGenerationStatus = async (pollingKey: string): Promise<AiTranscriptGenerationResponse> => {
  return (await window.PMW.readLocal('aitranscript/getAiTranscriptGenerationStatus', {
    pollingKey: pollingKey,
  })) as AiTranscriptGenerationResponse;
};

const generateAiTranscript = async (
  audioHashedFilename: string,
  trimStartTime: number,
  trimEndTime: number,
  mediaType: GeneratedFromType,
  loopCycles = 1,
  speed = 1,
  language = AiTranscriptSupportedLanguage.ENGLISH
): Promise<string> => {
  return (await window.PMW.writeLocal('aitranscript/generateAiTranscriptAndGetSubtitles', {
    hashedFilename: audioHashedFilename,
    startTime: trimStartTime,
    endTime: trimEndTime,
    mediaType,
    loopCycles,
    speed,
    language,
  })) as Promise<string>;
};

export const replaceExistingTranscriptOnPosterForGeneratedAiTranscript = async (
  existingTranscriptItems: TranscriptItemObject[],
  uidForTranscriptToAdd: string,
  generatedTranscript: GeneratedAiTranscriptData,
  onPosterStartTime: number,
  generatedFrom: TranscriptGeneratedFrom,
  selectOnAdd: boolean
): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  if (!existingTranscriptItems.length) {
    return addNewTranscriptToPosterForGeneratedAiTranscript(uidForTranscriptToAdd, generatedTranscript, onPosterStartTime, generatedFrom, selectOnAdd);
  }

  const orderedPages = getOrderedPages(poster);
  const subtitlesPerPage = splitGeneratedSubtitlesAcrossPages(generatedTranscript, onPosterStartTime, orderedPages);

  const addPromises: Promise<void>[] = [];
  let isFirstItem = true;

  // Get a set of page IDs that have existing transcript items
  const pagesWithExistingItems = new Set<string>();
  for (const item of existingTranscriptItems) {
    const page = poster.getPageForItem(item.uid);
    if (page) {
      pagesWithExistingItems.add(page.hashedID);
    }
  }

  // Process pages that have new subtitles
  for (const {id: pageId} of orderedPages) {
    const pageSubs = subtitlesPerPage[pageId];
    const page = poster.pages.pagesHashMap[pageId];

    if (!pageSubs?.length) {
      continue;
    }

    const existingItemOnPage = existingTranscriptItems.find((item) => poster.getPageForItem(item.uid)?.hashedID === pageId);

    if (existingItemOnPage) {
      // Replace existing item on this page
      addPromises.push(
        page.items.replaceItems.replaceTranscript(
          existingItemOnPage.uid,
          isFirstItem ? uidForTranscriptToAdd : getUniqueString(),
          {
            areAIGenerated: true,
            type: ElementDataType.TRANSCRIPT,
            subtitles: pageSubs,
          },
          {
            selectOnAdd: isFirstItem ? selectOnAdd : false,
            optionsToAdd: {generatedFrom},
          }
        )
      );
      pagesWithExistingItems.delete(pageId); // Mark as processed
    } else {
      // Add new item to this page
      addPromises.push(
        page.items.addItems.addTranscriptItem(
          {
            areAIGenerated: true,
            type: ElementDataType.TRANSCRIPT,
            subtitles: pageSubs,
          },
          {
            optionsToAdd: {uid: isFirstItem ? uidForTranscriptToAdd : getUniqueString(), generatedFrom},
            selectOnAdd: isFirstItem ? selectOnAdd : false,
          }
        )
      );
    }
    isFirstItem = false;
  }

  // Remove any existing transcript items on pages that no longer have subtitles
  for (const pageId of pagesWithExistingItems) {
    const page = poster.pages.pagesHashMap[pageId];
    const existingItemOnPage = existingTranscriptItems.find((item) => poster.getPageForItem(item.uid)?.hashedID === pageId);
    if (page && existingItemOnPage) {
      page.items.removeItem(existingItemOnPage.uid, false);
    }
  }

  await Promise.all(addPromises);
};

export const addNewTranscriptToPosterForGeneratedAiTranscript = async (
  uidForTranscriptToAdd: string,
  generatedTranscript: GeneratedAiTranscriptData,
  onPosterStartTime: number,
  generatedFrom: TranscriptGeneratedFrom,
  selectOnAdd: boolean
): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  const orderedPages = getOrderedPages(poster);
  const subtitlesPerPage = splitGeneratedSubtitlesAcrossPages(generatedTranscript, onPosterStartTime, orderedPages);

  const pageKeys = Object.keys(subtitlesPerPage);
  if (!pageKeys.length) {
    return;
  }

  const addPromises: Promise<void>[] = [];
  let isFirst = true;

  for (const {id: pageId} of orderedPages) {
    const pageSubs = subtitlesPerPage[pageId];
    if (!pageSubs?.length) {
      continue;
    }
    const page = poster.pages.pagesHashMap[pageId];
    addPromises.push(
      page.items.addItems.addTranscriptItem(
        {
          areAIGenerated: true,
          type: ElementDataType.TRANSCRIPT,
          subtitles: pageSubs,
        },
        {
          optionsToAdd: {uid: isFirst ? uidForTranscriptToAdd : getUniqueString(), generatedFrom},
          selectOnAdd: isFirst ? selectOnAdd : false,
        }
      )
    );
    isFirst = false;
  }
  await Promise.all(addPromises);
};

/**
 * Returns ordered pages (id + start time) sorted by start time.
 */
const getOrderedPages = (poster: Poster): OrderedPage[] => {
  const pageIds: string[] = poster.pages.pageOrder.length > 0 ? [...poster.pages.pageOrder] : Object.keys(poster.pages.pagesHashMap);
  const combined: OrderedPage[] = pageIds.map((id) => ({id, start: poster.pages.getStartTimeForPage(id)}));
  combined.sort((a, b) => a.start - b.start);
  return combined;
};

/**
 * Finds the page whose time range contains the given absolute time.
 */
const getPageIdForTimeFromOrderedPages = (orderedPages: OrderedPage[], time: number): string | undefined => {
  for (let i = 0; i < orderedPages.length; i++) {
    const current = orderedPages[i];
    const next = orderedPages[i + 1];
    const isInPage = i === orderedPages.length - 1 ? time >= current.start : time >= current.start && time < next.start;
    if (isInPage) {
      return current.id;
    }
  }
  return undefined;
};

/**
 * Builds per-page subtitle fragments for a single generated subtitle by splitting at word granularity.
 */
const buildFragmentsForGeneratedSubtitle = (
  generatedSubtitle: GeneratedTranscriptSubtitlesData,
  onPosterStartTime: number,
  orderedPages: OrderedPage[]
): Record<string, AddSubtitleData[]> => {
  const fragments: Record<string, AddSubtitleData[]> = {};
  const pageWordsMap: Record<string, AddWordData[]> = {};

  // Build a map from page ID to start time for O(1) lookup
  const pageIdToStartTime: Record<string, number> = {};
  for (const page of orderedPages) {
    pageIdToStartTime[page.id] = page.start;
  }

  for (const word of generatedSubtitle.words) {
    const absoluteWordStart = word.start + onPosterStartTime;
    const absoluteWordEnd = word.end + onPosterStartTime;
    const pageIdForWord = getPageIdForTimeFromOrderedPages(orderedPages, absoluteWordStart);
    if (pageIdForWord) {
      const pageStartTime = pageIdToStartTime[pageIdForWord] ?? 0;
      const relativeWordStart = Math.max(0, absoluteWordStart - pageStartTime);
      const relativeWordEnd = Math.max(relativeWordStart, absoluteWordEnd - pageStartTime);
      const addWord: AddWordData = {
        id: word.id,
        startTime: relativeWordStart,
        endTime: relativeWordEnd,
        text: word.text,
      };
      (pageWordsMap[pageIdForWord] = pageWordsMap[pageIdForWord] || []).push(addWord);
    }
  }

  for (const [pageId, words] of Object.entries(pageWordsMap)) {
    if (!words.length) {
      continue;
    }
    const fragmentSubtitle: AddSubtitleData = {
      id: generatedSubtitle.id,
      startTime: words[0].startTime,
      endTime: words[words.length - 1].endTime,
      text: words.map((w) => w.text).join(' '),
      words,
    };
    (fragments[pageId] = fragments[pageId] || []).push(fragmentSubtitle);
  }

  return fragments;
};

/**
 * Splits all generated subtitles across pages, returning a map of pageId to array of AddSubtitleData.
 */
const splitGeneratedSubtitlesAcrossPages = (
  generatedTranscript: GeneratedAiTranscriptData,
  onPosterStartTime: number,
  orderedPages: OrderedPage[]
): Record<string, AddSubtitleData[]> => {
  const subtitlesPerPage: Record<string, AddSubtitleData[]> = {};
  for (const generatedSubtitle of generatedTranscript.subtitles) {
    const fragments = buildFragmentsForGeneratedSubtitle(generatedSubtitle, onPosterStartTime, orderedPages);
    for (const [pageId, fragmentSubs] of Object.entries(fragments)) {
      (subtitlesPerPage[pageId] = subtitlesPerPage[pageId] || []).push(...fragmentSubs);
    }
  }
  return subtitlesPerPage;
};

export const showAiSubtitlesUpsellDialog = (): void => {
  void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_SUBTITLES);
};

export const doesTranscriptHaveUserEditedSubtitles = (transcript: TranscriptItemObject): boolean => {
  for (const [, subtitle] of Object.entries(transcript.subtitlesHashmap)) {
    if (subtitle.hasUserEdited) {
      return true;
    }
  }

  return false;
};

export const hasSpeedBeenChangedAfterAiTranscriptGenerationForItems = (itemSpeed: number, transcripts: TranscriptItemObject[]): boolean => {
  if (!transcripts.length) {
    return true;
  }

  return transcripts.some((transcript) => hasSpeedBeenChangedAfterAiTranscriptGeneration(itemSpeed, transcript.generatedFrom?.speed));
};

export const hasSpeedBeenChangedAfterAiTranscriptGeneration = (itemSpeed: number, transcriptGeneratedFromSpeed?: number): boolean => {
  if (!transcriptGeneratedFromSpeed) {
    return true;
  }

  return itemSpeed !== transcriptGeneratedFromSpeed;
};

export const hasLoopCountChangedAfterAiTranscriptGenerationForVideoItems = (itemLoopCycles: number, transcripts: TranscriptItemObject[]): boolean => {
  if (!transcripts.length) {
    return true;
  }

  return transcripts.some((transcript) => hasLoopCountChangedAfterAiTranscriptGenerationForVideo(itemLoopCycles, transcript.generatedFrom?.loopCycles));
};

export const hasLoopCountChangedAfterAiTranscriptGenerationForVideo = (itemLoopCycles: number, transcriptGeneratedFromLoopCycles?: number): boolean => {
  if (!transcriptGeneratedFromLoopCycles) {
    return true;
  }

  return itemLoopCycles !== transcriptGeneratedFromLoopCycles;
};

export const getAiTranscriptParamsForAudio = (audioItem: AudioItemObject): AiTranscriptForItemWithParams => {
  return {
    uid: audioItem.uid,
    type: 'audio',
    hashedFilename: audioItem.hashedFilename,
    startTime: audioItem.audioPlayer.trim.startTime,
    endTime: audioItem.audioPlayer.trim.endTime,
    onPosterStartTime: audioItem.onPosterStartTime,
    loopCycles: 1,
    speed: audioItem.audioPlayer.speed,
  };
};

export const getAiTranscriptParamsForVideo = (videoItem: VideoItemObject, loopCycles: number): AiTranscriptForItemWithParams => {
  return {
    uid: videoItem.uid,
    type: 'video',
    hashedFilename: videoItem.hashedFilename,
    startTime: videoItem.startTime,
    endTime: videoItem.endTime,
    onPosterStartTime: 0,
    loopCycles,
    speed: 1,
  };
};

export const getSubtitlesWordArrayAfterTextEdited = (editedText: string, subtitleStartTime: number, subtitleEndTime: number): WordObject[] => {
  const wordsToMake = editedText.trim().split(' ');
  const numberOfWords = wordsToMake.length;
  const durationOfEachWord = (subtitleEndTime - subtitleStartTime) / numberOfWords;

  const newWords: WordObject[] = [];

  let startTime = subtitleStartTime;

  for (let i = 0; i < numberOfWords; i++) {
    const cleanWord = removePunctuationFromWord(wordsToMake[i]);

    newWords.push({
      id: getUniqueString(),
      text: cleanWord,
      startTime,
      endTime: startTime + durationOfEachWord,
    });

    startTime = startTime + durationOfEachWord + 0.01;
  }

  return newWords;
};

export const addNewManualTranscript = async (): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  const currentPage = poster.getCurrentPage();

  const subtitles: AddSubtitleData[] = [
    {
      id: getUniqueString(),
      startTime: 0,
      endTime: 3,
      text: 'The quick brown fox',
      words: [
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'The',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'quick',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'brown',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'fox',
        },
      ],
    },
    {
      id: getUniqueString(),
      startTime: 3 + MIN_TIME_GAP_FOR_TRANSCRIPT,
      endTime: 5,
      text: 'jumps over the lazy dog',
      words: [
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'jumps',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'over',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'the',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'lazy',
        },
        {
          id: getUniqueString(),
          startTime: 0,
          endTime: 0,
          text: 'dog',
        },
      ],
    },
  ];

  await currentPage.items.addItems.addTranscriptItem(
    {
      areAIGenerated: false,
      type: ElementDataType.TRANSCRIPT,
      subtitles,
    },
    {
      optionsToAdd: {},
      selectOnAdd: true,
      checkForDurationUpdate: true,
    }
  );
};

const getNameForVideoItem = (videoOrder = 0): string => {
  return `Video ${videoOrder}`;
};

export const getSelectableItemDataForVideoData = (item: VideoItemObject, loopCycles: number, videoOrder: number): SelectableItemWithAudio => {
  return {
    duration: item.endTime - item.startTime,
    name: getNameForVideoItem(videoOrder),
    imageUrl: repoVideoThumbImageURL(item.hashedFilename, item.fileExtension),
    id: item.uid,
    isSelected: false,
    type: 'video',
    sourceProperties: {
      uid: item.uid,
      type: 'video',
      hashedFilename: item.hashedFilename,
      startTime: item.startTime,
      endTime: item.endTime,
      onPosterStartTime: 0,
      speed: 1,
      loopCycles: loopCycles,
    },
  };
};

export const getSelectableItemDataForAudioData = (item: AudioItemObject): SelectableItemWithAudio => {
  return {
    duration: item.audioPlayer.trim.endTime - item.audioPlayer.trim.startTime,
    name: item.name,
    imageUrl: getRandomThumbnailImageForAudioItem(),
    id: item.uid,
    isSelected: false,
    type: 'audio',
    sourceProperties: {
      uid: item.uid,
      type: 'audio',
      hashedFilename: item.hashedFilename,
      startTime: item.audioPlayer.trim.startTime,
      endTime: item.audioPlayer.trim.endTime,
      onPosterStartTime: item.onPosterStartTime,
      speed: item.audioPlayer.speed,
      loopCycles: 1,
    },
  };
};

export const getAiSubtitlesPanelHeightClass = (aiSubtitleState: AiSubtitleState, itemsCount: number, generationsCount: number, isPremium = false): string => {
  if (aiSubtitleState === AiSubtitleState.IDLE) {
    const numberOfItems = itemsCount;
    const baseStyle = numberOfItems <= 3 ? styles.aiSubtitlesChooseMediaLevel1 : numberOfItems <= 7 ? styles.aiSubtitlesChooseMediaLevel2 : styles.aiSubtitlesChooseMediaLevel3;

    return `${baseStyle} ${isPremium ? styles.premium : ''}`;
  }

  const numberOfItemsGenerating = generationsCount;
  if (numberOfItemsGenerating === 1) {
    return styles.aiSubtitlesGeneratingLevel1;
  }

  if (numberOfItemsGenerating === 2) {
    return styles.aiSubtitlesGeneratingLevel2;
  }

  if (numberOfItemsGenerating === 3) {
    return styles.aiSubtitlesGeneratingLevel3;
  }

  if (numberOfItemsGenerating === 4) {
    return styles.aiSubtitlesGeneratingLevel4;
  }

  return styles.aiSubtitlesGeneratingLevel5;
};
